<script setup lang="ts">
import {
  useSeoBase,
  useJsonLd,
  orgNode,
  localBusinessNode,
  breadcrumbNode,
} from "../../composables/useSeo";
import { usePhoneNumber } from "../../composables/usePhoneNumber";
import { useAnalytics } from "../../composables/useAnalytics";

const { phoneNumber, phoneNumberPretty, phoneNumberTel } = usePhoneNumber();
const { trackConversion } = useAnalytics();
const { servicePrice, serviceLabel } = usePricing();
const displayedServicePrice = servicePrice("deck-removal");
const displayedServiceLabel = serviceLabel("deck-removal");

useSeoBase({
  title: "Deck Removal | Junk Guys MN - Professional Deck Demolition",
  description:
    "Professional deck removal and demolition services in the Twin Cities. Safe removal of old, damaged, or unwanted decks. Same-day service available.",
  canonical: "https://junkguysmn.com/services/deck-removal",
  ogImage: "https://junkguysmn.com/og.jpg",
});

useJsonLd([
  orgNode(),
  localBusinessNode(),
  breadcrumbNode([
    { name: "Home", url: "https://junkguysmn.com" },
    { name: "Services", url: "https://junkguysmn.com/services" },
    {
      name: "Deck Removal",
      url: "https://junkguysmn.com/services/deck-removal",
    },
  ]),
  {
    "@type": "Service",
    name: "Deck Removal",
    description: "Professional deck removal and demolition services",
    provider: {
      "@type": "Organization",
      name: "Junk Guys MN",
    },
    areaServed: {
      "@type": "Place",
      name: "Minneapolis-St. Paul Twin Cities Metro Area",
    },
    serviceType: "Deck Removal",
  },
]);
</script>

<template>
  <div class="min-h-screen bg-slate-50">
    <!-- Hero Section -->
    <section
      class="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white"
    >
      <div class="mx-auto max-w-7xl px-4 py-16 lg:py-24">
        <div class="text-center max-w-3xl mx-auto">
          <h1 class="text-4xl lg:text-6xl font-bold mb-6">
            Deck Removal Services
          </h1>
          <p class="text-xl lg:text-2xl text-slate-200 leading-relaxed">
            Professional deck demolition and disposal across the Twin Cities. We
            safely remove old, damaged, or unwanted decks and restore your
            outdoor space.
          </p>
          <div class="mt-8 flex flex-wrap justify-center gap-4">
            <a
              :href="phoneNumberTel"
              @click="trackConversion"
              class="inline-flex items-center justify-center px-8 py-4 bg-primary-main hover:bg-primary-dark text-white font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
            >
              📞 Call {{ phoneNumberPretty }}
            </a>
            <NuxtLink
              to="/book"
              class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-slate-900 transition-all duration-200"
            >
              📅 Book Online
            </NuxtLink>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Overview -->
    <section class="py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 class="text-3xl font-bold text-slate-900 mb-6">
              What We Remove
            </h2>
            <p class="text-lg text-slate-600 mb-6">
              Our professional deck removal service handles all types of outdoor
              decks and patios throughout Minneapolis, St. Paul, and the Twin
              Cities. We provide safe demolition, complete debris removal, and
              site restoration for your outdoor space transformation.
            </p>
            <div class="grid grid-cols-2 gap-4">
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Wooden Decks</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Composite Decks</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">PVC Decks</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Deck Railings</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Stairs & Posts</span>
              </div>
              <div class="flex items-center gap-3">
                <div class="w-2 h-2 bg-primary-main rounded-full"></div>
                <span class="text-slate-700">Concrete Footings</span>
              </div>
            </div>
          </div>
          <div class="bg-white rounded-2xl shadow-lg p-8">
            <h3 class="text-2xl font-bold text-slate-900 mb-6">
              Service Details
            </h3>
            <div class="space-y-4">
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700">{{
                  displayedServiceLabel
                }}</span>
                <span class="text-2xl font-bold text-primary-main">{{
                  displayedServicePrice
                }}</span>
              </div>
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700"
                  >Typical Duration</span
                >
                <span class="text-lg text-slate-600">3-8 hours</span>
              </div>
              <div
                class="flex justify-between items-center p-4 bg-slate-50 rounded-lg"
              >
                <span class="font-semibold text-slate-700"
                  >Site Restoration</span
                >
                <span class="text-green-600 font-semibold">Included</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Why Choose Us -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Why Choose Our Deck Removal
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional demolition with minimal property damage and complete
            cleanup
          </p>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🛡️</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Safe Demolition
            </h3>
            <p class="text-slate-600">
              Our experienced crew carefully dismantles decks section by
              section, protecting your property and landscaping.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🧹</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Complete Cleanup
            </h3>
            <p class="text-slate-600">
              We remove all debris, clean the area, and prepare the site for
              your new outdoor project.
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🚚</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Same-Day Service
            </h3>
            <p class="text-slate-600">
              Often available for urgent deck removal needs. Call early to check
              same-day availability.
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Deck Removal Pricing
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Transparent pricing based on size and complexity
          </p>
        </div>
        <div class="grid md:grid-cols-3 gap-8">
          <div class="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div class="text-4xl mb-4">🏠</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Small Decks</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ displayedServicePrice }}
            </div>
            <p class="text-slate-600 mb-4">12x16 ft or smaller</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Basic deck removal</li>
              <li>• Railing and stairs</li>
              <li>• Debris cleanup</li>
              <li>• Site preparation</li>
            </ul>
          </div>
          <div
            class="bg-white rounded-2xl shadow-lg p-8 text-center border-2 border-primary-main"
          >
            <div class="text-4xl mb-4">🏗️</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Medium Decks</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ displayedServicePrice }}
            </div>
            <p class="text-slate-600 mb-4">16x20 ft range</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Complete removal</li>
              <li>• Foundation removal</li>
              <li>• Site restoration</li>
              <li>• Landscaping protection</li>
            </ul>
          </div>
          <div class="bg-white rounded-2xl shadow-lg p-8 text-center">
            <div class="text-4xl mb-4">🏢</div>
            <h3 class="text-xl font-bold text-slate-900 mb-2">Large Decks</h3>
            <div class="text-3xl font-bold text-primary-main mb-2">
              {{ displayedServicePrice }}
            </div>
            <p class="text-slate-600 mb-4">20x24 ft or larger</p>
            <ul class="text-left space-y-2 text-sm text-slate-600">
              <li>• Large demolition</li>
              <li>• Heavy equipment</li>
              <li>• Complete restoration</li>
              <li>• Custom quote</li>
            </ul>
          </div>
        </div>
      </div>
    </section>

    <!-- Pricing Factors -->
    <section class="bg-white py-16 border-t border-slate-200">
      <div class="mx-auto max-w-4xl px-4">
        <div class="text-center mb-10">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            What Affects Deck Removal Cost?
          </h2>
          <p class="text-lg text-slate-600">
            Our transparent pricing considers these key factors
          </p>
        </div>

        <div class="space-y-4">
          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Deck Size & Height</h3>
              <p class="text-slate-600">
                A small ground-level deck costs less than a large second-story
                deck. Square footage and elevation affect labor complexity.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Material Type</h3>
              <p class="text-slate-600">
                Pressure-treated wood, composite decking, and cedar each have
                different weights and fastening systems that affect removal
                time.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">
                Site Access & Layout
              </h3>
              <p class="text-slate-600">
                Backyard access, proximity to house, and landscaping obstacles
                affect how we remove materials from the site.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">
                Foundation & Demolition Scope
              </h3>
              <p class="text-slate-600">
                Removing deck boards vs. complete demo including posts,
                footings, and concrete requires different tools and labor.
              </p>
            </div>
          </div>

          <div class="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
            <div
              class="w-8 h-8 bg-primary-main/10 rounded-full flex items-center justify-center flex-shrink-0 mt-1"
            >
              <svg
                class="w-5 h-5 text-primary-main"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fill-rule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clip-rule="evenodd"
                />
              </svg>
            </div>
            <div>
              <h3 class="font-bold text-slate-900 mb-1">Same-Day Service</h3>
              <p class="text-slate-600">
                Need your deck removed urgently? We offer same-day service when
                our schedule allows.
              </p>
            </div>
          </div>
        </div>

        <div
          class="mt-10 p-6 bg-primary-main/10 border-l-4 border-primary-main rounded-r-lg"
        >
          <p class="text-lg text-slate-900">
            <strong>Ready for an accurate quote?</strong> Call
            <a
              :href="phoneNumberTel"
              class="font-bold text-primary-main hover:text-primary-dark underline"
              >(612) 424-0609</a
            >
            for a fast estimate. We provide upfront pricing before we start-no
            surprises.
          </p>
        </div>
      </div>
    </section>

    <!-- Why Choose Junk Guys MN -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Why Choose Junk Guys MN
          </h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Professional deck removal service you can trust
          </p>
        </div>
        <div class="grid md:grid-cols-4 gap-8">
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">🏠</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Locally Owned
            </h3>
            <p class="text-slate-600">
              Family-owned business serving the Twin Cities communities
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">⚡</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Same-Day Service
            </h3>
            <p class="text-slate-600">
              Quick response with same-day availability when you need it
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">♻️</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Eco-Friendly
            </h3>
            <p class="text-slate-600">
              We donate and recycle whenever possible to reduce landfill waste
            </p>
          </div>
          <div class="text-center">
            <div
              class="w-16 h-16 bg-primary-main/10 rounded-full flex items-center justify-center mx-auto mb-4"
            >
              <span class="text-2xl">💰</span>
            </div>
            <h3 class="text-lg font-semibold text-slate-900 mb-2">
              Upfront Pricing
            </h3>
            <p class="text-slate-600">
              Transparent quotes with no hidden fees or surprises
            </p>
          </div>
        </div>
      </div>
    </section>

    <!-- Service Areas -->
    <section class="bg-white py-16">
      <div class="mx-auto max-w-7xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">Service Areas</h2>
          <p class="text-lg text-slate-600 max-w-2xl mx-auto">
            Providing deck removal services throughout Minneapolis, St. Paul,
            and surrounding Twin Cities communities
          </p>
        </div>
        <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 max-w-4xl mx-auto">
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/minneapolis/deck-removal"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Minneapolis</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/saint-paul/deck-removal"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Saint Paul</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/bloomington"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Bloomington</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/eden-prairie"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Eden Prairie</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/maple-grove"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Maple Grove</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/brooklyn-park"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Brooklyn Park</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/city/lakeville"
              class="text-primary-main hover:text-primary-dark font-semibold"
              >Lakeville</NuxtLink
            >
          </div>
          <div class="bg-slate-50 p-4 rounded-lg text-center">
            <NuxtLink
              to="/services"
              class="text-slate-700 hover:text-primary-main font-semibold"
              >+ More Areas</NuxtLink
            >
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ -->
    <section class="bg-slate-50 py-16">
      <div class="mx-auto max-w-4xl px-4">
        <div class="text-center mb-12">
          <h2 class="text-3xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p class="text-lg text-slate-600">
            Common questions about deck removal
          </p>
        </div>
        <div
          class="bg-white rounded-2xl shadow-sm border border-slate-200 divide-y divide-slate-200"
        >
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  How long does deck removal take?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Most deck removals take 3-8 hours, depending on size and
                complexity. We'll provide a specific timeline during your
                assessment.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Will you damage my landscaping?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                We take great care to protect your landscaping. We use
                protective materials and work carefully to minimize any
                disruption to your yard.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do you remove the concrete footings?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Yes, we can remove concrete footings and posts. This is included
                in our medium and large deck removal packages.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  How much does deck removal cost?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Pricing depends on deck size, height, material, and access. Deck
                demolition requires an on-site estimate. Call
                <a
                  :href="phoneNumberTel"
                  class="font-semibold text-primary-main hover:text-primary-dark underline"
                  >(612) 424-0609</a
                >
                for an accurate quote.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do you offer same-day deck removal?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                Yes! Same-day deck removal is available throughout the Twin
                Cities when our schedule allows. Contact us early for best
                availability.
              </div>
            </div>
          </details>
          <details class="group">
            <summary
              class="cursor-pointer list-none p-6 hover:bg-slate-50 transition-colors duration-200 rounded-lg"
            >
              <div class="flex items-center justify-between">
                <span
                  class="text-lg font-semibold text-slate-900 group-open:text-primary-main transition-colors duration-200"
                >
                  Do I need to be present during deck removal?
                </span>
                <div
                  class="flex h-8 w-8 items-center justify-center rounded-full bg-slate-100 group-open:bg-primary-main/10 transition-all duration-200 flex-shrink-0 ml-4"
                >
                  <svg
                    class="h-5 w-5 text-slate-600 group-open:text-primary-main transform transition-transform duration-200 group-open:rotate-180"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    ></path>
                  </svg>
                </div>
              </div>
            </summary>
            <div class="px-6 pb-6">
              <div
                class="pt-4 text-slate-600 leading-relaxed text-base border-t border-slate-100"
              >
                While we prefer you're present for final approval and access,
                it's not required. Many Minneapolis-St. Paul homeowners arrange
                for us to work independently with payment arranged in advance.
              </div>
            </div>
          </details>
        </div>
      </div>
    </section>

    <!-- Final CTA -->
    <section class="bg-primary-main text-white py-16">
      <div class="mx-auto max-w-7xl px-4 text-center">
        <h2 class="text-3xl lg:text-4xl font-bold mb-4">
          Need Your Deck Removed?
        </h2>
        <p class="text-xl text-white mb-8 max-w-2xl mx-auto">
          Transform your outdoor space with professional deck removal services.
          Same-day service available!
        </p>
        <div class="flex flex-wrap justify-center gap-4">
          <a
            :href="phoneNumberTel"
            class="inline-flex items-center justify-center px-8 py-4 bg-white text-primary-main font-semibold rounded-lg shadow-lg hover:shadow-xl transition-all duration-200"
          >
            📞 Call {{ phoneNumberPretty }}
          </a>
          <NuxtLink
            to="/book"
            class="inline-flex items-center justify-center px-8 py-4 border-2 border-white text-white font-semibold rounded-lg hover:bg-white hover:text-primary-main transition-all duration-200"
          >
            📅 Book Online
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>
